import {inject, Injectable} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {Query, VismaOrderDetailV2} from '../interfaces/generated/graphql';

export type OrderLineItem = {
  id: string;
  date: string;
  productTitle: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  sku: string;
  unitType: string;
  storeName: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrderHistoryService {
  private apollo = inject(Apollo);

  formatOrderToLineItems(order: VismaOrderDetailV2): OrderLineItem[] {
    if (!order) {
      return [];
    }
    return order.orderLines?.map((orderLine) => {
      if (!orderLine) {
        return;
      }
      return {
        id: orderLine.id,
        date: order.orderDate,
        productTitle: orderLine.productTitle,
        quantity: orderLine.quantity,
        unitPrice: orderLine.unitPrice,
        lineTotal: orderLine.lineTotal,
        sku: orderLine.sku,
        unitType: orderLine.unitType,
        storeName: order.storeName ?? 'Ukendt'
      }
    }) as OrderLineItem[];
  }

  getHumanStoreName(storeName: string) {
    if (!storeName) {
      return 'Ukendt';
    }

    const mapping: { [key: string]: string } = {
      'dk_webshop': 'Supervin.dk',
      'supervin_hjorring': 'Supervin Hjørring',
      'vivino_dk': 'Vivino via Supervin',
      'webshop_return': 'Supervin.dk returordre',
      'supervin_business': 'Supervin B2B Afdeling',
      'supervin_customer_service': 'Supervin Kundeservice'
    };
    return mapping[storeName] ?? storeName;
  }

  getHumanStatus(orderStatus?: string | null) {
    if (!orderStatus) {
      return '';
    }

    const mapping: { [key: string]: string } = {
      'store_purchase': 'Købt i butik',
      'returned': 'Returneret',
      'cancelled': 'Annulleret',
      'accepted': 'Ordren er modtaget',
      'ready_for_delivery': 'Ordren er afsendt',
      'in_transit': 'På vej med leverandør',
      'delivered': 'Leveret',
      'unknown': 'Status ukendt',
    }

    return mapping[orderStatus] ?? orderStatus ?? '';
  }

  getOrdersByVismaCustomerId(customerId: string, pageIndex: number) {
    return this.apollo.query<Query>({
      query: gql`
        query getVismaOrderHistoryByVismaCustomerId($vismaCustomerId: ID!, $pageNumber: Int) {
          getVismaOrderHistoryByVismaCustomerId(vismaCustomerId: $vismaCustomerId, pageNumber: $pageNumber) {
            totalItems
            totalPages
            items {
              id
              storeName
              orderDate
              orderTotal
              orderStatus
              orderComment
              vismaOrderNumber
              deliveryPrice
              discounts {
                id
                name
                amount
              }
              orderLines {
                id
                productTitle
                quantity
                unitPrice
                lineTotal
                sku
                unitType
              }
            }
          }
        }
      `,
      variables: {
        vismaCustomerId: customerId,
        pageNumber: pageIndex
      }
    }).toPromise().then(result => result?.data.getVismaOrderHistoryByVismaCustomerId);
  }
}
